/**
 * Which markets are on sale, and why each hidden one is hidden.
 *
 * READ AND WRITE LIVE ON DIFFERENT ENDPOINTS, deliberately. The grid comes from
 * `GET /admin/markets/visibility` (readable by any staff role); the change goes
 * out as `hiddenMarkets` on `PUT /admin/params`, which is WRITE-gated. So a
 * support user can answer "is AFL totals on sale?" without being able to change
 * the answer, and the refusal they get if they try is the API's, not ours.
 *
 * THE TWO THINGS THIS SCREEN MUST NOT DO:
 *
 * 1. Hide a rule that matches no live cell. `hiddenMarkets` is rendered as its
 *    own list ABOVE the grid, because a rule for a market the catalogue has not
 *    reported this week has no checkbox to untick and would otherwise be
 *    invisible AND unremovable — an operator would be left with a market
 *    suppressed by something they cannot see.
 *
 * 2. Pretend a wildcard is a checkbox. A cell hidden by `soccer:*` looks
 *    identical to one hidden by `soccer:totals`, but unticking it means
 *    completely different things — the second removes one entry, the first
 *    requires breaking the wildcard into every sibling it covered. The server
 *    returns `hiddenBy` precisely so the console can say which, and this page
 *    REFUSES the untick in the wildcard case rather than guessing.
 *
 * Stale cells are greyed, never dropped: a seasonal market coming back on the
 * board is one of the most common reasons to open this page at all.
 *
 * Both refusals above, and the staleness reading, live as pure functions in
 * `./logic` so they can be tested without rendering a grid.
 */
import { useMemo, useState } from 'react';
import { useMarketVisibility, useAdminParams, useUpdateAdminParams } from '@/hooks/useAdmin';
import { Pill } from '@/components/ui/StatusPill';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ApiClientError } from '@/api/client';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import {
  applyVisibilityToggle,
  cellKey,
  deriveNextHidden,
  isStale,
  isWildcardHidden,
  ruleMatchesLiveCell,
  type PendingVisibility,
} from './logic';

export function MarketVisibilityPage() {
  const grid = useMarketVisibility();
  const params = useAdminParams();
  const update = useUpdateAdminParams();

  /** Pending edits, keyed `sport:market`. Absent = no change from server state. */
  const [pending, setPending] = useState<PendingVisibility>({});
  const [note, setNote] = useState('');

  /*
    Memoised because `?? []` allocates a FRESH empty array on every render, which
    would re-run the grouping below each time even though nothing changed.
  */
  const cells = useMemo(() => grid.data?.cells ?? [], [grid.data?.cells]);

  /** Group by sport so the grid reads as a board rather than a flat list. */
  const bySport = useMemo(() => {
    const map = new Map<string, { name: string; cells: typeof cells }>();
    for (const cell of cells) {
      const entry = map.get(cell.sportKey) ?? { name: cell.sportName, cells: [] };
      entry.cells.push(cell);
      map.set(cell.sportKey, entry);
    }
    return [...map.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [cells]);

  /**
   * The next hidden set, derived from the CURRENT stored rules plus the pending
   * ticks. Derived rather than held in state so a background refetch of the
   * params row cannot leave the draft describing a rule set that no longer
   * exists.
   */
  const nextHidden = useMemo(
    () => deriveNextHidden(params.data?.hiddenMarkets ?? [], pending),
    [params.data?.hiddenMarkets, pending],
  );

  const changedCount = Object.keys(pending).length;

  if (grid.isLoading || params.isLoading) return <AdminLoading label="Reading the board" />;
  if (grid.isError || !grid.data)
    return <AdminError error={grid.error} onRetry={() => void grid.refetch()} />;
  if (params.isError || !params.data)
    return <AdminError error={params.error} onRetry={() => void params.refetch()} />;

  const conflict =
    update.error instanceof ApiClientError && update.error.status === 409 ? update.error : null;

  function toggle(key: string, currentlyHidden: boolean, hiddenBy: string | null) {
    // The wildcard refusal and the undo-collapses-to-no-change rule both live
    // in applyVisibilityToggle — see ./logic for why each exists.
    setPending((prev) =>
      applyVisibilityToggle(prev, {
        key,
        currentlyHidden,
        hiddenBy,
        storedHidden: params.data?.hiddenMarkets ?? [],
      }),
    );
  }

  function onSave() {
    if (!params.data) return;
    update.mutate(
      {
        expectedVersion: params.data.version,
        hiddenMarkets: nextHidden,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: () => { setPending({}); setNote(''); void grid.refetch(); } },
    );
  }

  /*
    Age is measured against WHEN THE DATA WAS READ, not against a render-time
    clock. Reading Date.now() here would re-time the same payload on every
    unrelated re-render, so "synced 3 minutes ago" would drift without a refetch
    ever happening — an operator deciding whether the scheduler is alive would be
    reading the age of their own re-renders.
  */
  const now = grid.dataUpdatedAt;

  return (
    <>
      <AdminPageHeader
        title="Market visibility"
        subtitle="Unticking a market stops new bets on it immediately. Settlement of bets already placed is unaffected — this is a sales control, not a kill switch."
        actions={
          changedCount > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setPending({})}>
              Discard {changedCount} change{changedCount === 1 ? '' : 's'}
            </Button>
          ) : undefined
        }
      />

      {conflict && (
        <div className="mb-5 rounded-2xl border border-brand-gold/30 bg-brand-gold/[0.07] p-4">
          <p className="text-sm font-semibold text-brand-gold">Someone else changed the board</p>
          <p className="mt-1 text-sm leading-relaxed text-white/60">
            Nothing was written. Your ticks are still here — reload the current rules, check they
            still make sense together, then save again.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => { update.reset(); void params.refetch(); void grid.refetch(); }}
          >
            Reload current rules
          </Button>
        </div>
      )}

      {/* Rule list FIRST — see rule 1 in the header. A rule matching no live
          cell has no checkbox and would otherwise be unremovable. */}
      <Card title="Stored rules" className="mb-5">
        {nextHidden.length === 0 ? (
          <p className="text-sm text-white/40">
            No rules — every market the catalogue reports is on sale.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {nextHidden.map((rule) => {
              const matches = ruleMatchesLiveCell(rule, cells);
              return (
                <span
                  key={rule}
                  className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 font-mono text-[11px] ${
                    matches
                      ? 'border-white/12 bg-white/[0.05] text-white/70'
                      : 'border-brand-gold/25 bg-brand-gold/[0.06] text-brand-gold'
                  }`}
                >
                  {rule}
                  {!matches && <span className="font-sans text-[10px]">no live market</span>}
                </span>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs leading-relaxed text-white/35">
          A rule in gold matches nothing the catalogue has reported. That is usually a seasonal
          market, occasionally a typo — either way it stays in force until removed.
        </p>
      </Card>

      <div className="space-y-5">
        {bySport.map(([sportKey, group]) => (
          <Card key={sportKey} title={group.name}>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {group.cells.map((cell) => {
                const key = cellKey(cell);
                const hidden = key in pending ? pending[key]! : cell.hidden;
                const edited = key in pending;
                const wildcarded = isWildcardHidden(cell);
                const stale = isStale(cell.lastSeenAt, now);

                return (
                  <label
                    key={key}
                    className={`flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 transition ${
                      edited
                        ? 'border-brand-teal/30 bg-brand-teal/[0.06]'
                        : 'border-white/[0.07] bg-white/[0.02] hover:bg-white/[0.04]'
                    } ${wildcarded ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={!hidden}
                      disabled={wildcarded}
                      onChange={() => toggle(key, hidden, cell.hiddenBy)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-brand-teal"
                    />
                    <span className="min-w-0">
                      <span className={`block text-sm ${stale ? 'text-white/45' : 'text-white/85'}`}>
                        {cell.marketName}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        {!cell.catalogueActive && <Pill tone="muted">not in catalogue</Pill>}
                        {stale && <Pill tone="muted">not seen in 24h</Pill>}
                        {wildcarded && (
                          <Pill tone="caution">hidden by {cell.hiddenBy}</Pill>
                        )}
                        {edited && <Pill tone="positive">edited</Pill>}
                      </span>
                      <span className="mt-1 block font-mono text-[10px] text-white/25">
                        {cell.providerIds.join(' · ') || 'no provider'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {changedCount > 0 && (
        <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-brand-navy2/95 p-4 backdrop-blur">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why? (recorded in the audit log)"
            className="min-w-[200px] flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/85 placeholder:text-white/25 focus:border-brand-teal/40 focus:outline-none"
          />
          <span className="text-sm text-white/45">
            {changedCount} market{changedCount === 1 ? '' : 's'} changed
          </span>
          <Button onClick={onSave} loading={update.isPending} disabled={conflict !== null}>
            Save
          </Button>
        </div>
      )}

      {/* A wildcard rule cannot be unticked cell-by-cell — say so once, here,
          rather than leaving the disabled checkbox to explain itself. */}
      <p className="mt-4 text-xs leading-relaxed text-white/30">
        A market hidden by a wildcard rule cannot be re-opened one cell at a time. Remove or narrow
        the rule instead — unticking here would silently re-open every sibling it covers.
      </p>
    </>
  );
}
